import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoMerchantClient } from "@redotech/redo-merchant-app-common/client";
import { RedoMerchantClientContext } from "@redotech/redo-merchant-app-common/client/context";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  ConversationStatus,
  MinimalConversation,
} from "@redotech/redo-model/conversation";
import { TagGroup } from "@redotech/redo-model/support/tag-group-zod-schema";
import { Team } from "@redotech/redo-model/team";
import { RedoCheckbox } from "@redotech/redo-web/arbiter-components/checkbox/redo-checkbox";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { memo, useEffect, useState } from "react";
import { bulkUpdateConversationsStatus } from "../client/conversations";
import { UpdateConversationStateContext } from "../context/update-conversations-context";
import {
  closeTicket,
  getNextBatch,
  validateConversationCanClose,
} from "../utils";
import * as closeTicketModalCss from "./close-ticket-modal.module.css";

export const handleCloseTickets = async ({
  client,
  conversations,
  setActiveConversation,
  team,
  inDetailView,
  cleanupFn,
  removeConversation,
  nextConversationInList,
  prevConversationInList,
}: {
  client: RedoMerchantClient;
  conversations: MinimalConversation[];
  setActiveConversation: (val: MinimalConversation | undefined) => void;
  team: Team;
  inDetailView: boolean;
  cleanupFn?: (clearSelected?: boolean) => void;
  removeConversation: (conversation: MinimalConversation) => void;
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
}) => {
  if (conversations.length === 1) {
    await closeTicket(
      client,
      conversations[0],
      setActiveConversation,
      team,
      inDetailView,
      nextConversationInList || prevConversationInList,
    );
    removeConversation(conversations[0]);
    amplitude.logEvent("close-conversation", {
      mode: "single",
      conversationIds: [conversations[0]._id],
      channels: [conversations[0].platform],
    });
  } else if (conversations.length > 1) {
    const conversationsToClose = conversations.filter(
      (conversation) => conversation.status !== ConversationStatus.CLOSED,
    );
    if (conversationsToClose.length > 0) {
      const conversationIds = conversationsToClose.map(
        (conversation) => conversation._id,
      );
      await bulkUpdateConversationsStatus(client, {
        conversationIds,
        status: ConversationStatus.CLOSED,
      });
      const channels = [
        ...new Set(
          conversationsToClose.map((conversation) => conversation.platform),
        ),
      ];
      for (const conversation of conversationsToClose) {
        removeConversation(conversation);
      }
      amplitude.logEvent("close-conversation", {
        mode: "multiple",
        conversationIds,
        channels,
      });
    }
  }
  cleanupFn?.(true);
};

export const CloseTicketModal = memo(function CloseTicketModal({
  open,
  setOpen,
  conversations,
  setActiveConversation,
  inDetailView,
  cleanupFn,
  selectAllInfo,
  nextConversationInList,
  prevConversationInList,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
  conversations: MinimalConversation[];
  setActiveConversation: (val: MinimalConversation | undefined) => void;
  inDetailView: boolean;
  cleanupFn?: (clearSelected?: boolean) => void;
  selectAllInfo?: {
    count: number;
    deselectedConversations: MinimalConversation[];
    bulkActionIterator: AsyncIterator<{ conversations: MinimalConversation[] }>;
    setBlockRefresh: (val: boolean) => void;
    closeImmediately: boolean;
  };
  nextConversationInList?: MinimalConversation;
  prevConversationInList?: MinimalConversation;
}) {
  const team = useRequiredContext(TeamContext);
  const client = useRequiredContext(RedoMerchantClientContext);
  const rpcClient = useRequiredContext(RedoMerchantRpcClientContext);
  const { removeConversation } = useRequiredContext(
    UpdateConversationStateContext,
  );
  const [conversationsCanClose, setConversationsCanClose] = useState<
    MinimalConversation[]
  >(
    conversations.filter(
      (conversation) => conversation.status !== ConversationStatus.CLOSED,
    ),
  );
  const [closingSelectAll, setClosingSelectAll] = useState(false);
  const [numDone, setNumDone] = useState(0);
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);
  const [missingTagGroups, setMissingTagGroups] = useState<TagGroup[]>([]);

  const validationResult = useLoad(async () => {
    if (!open || conversationsCanClose.length === 0 || selectAllInfo) {
      return { canClose: true, missingTagGroups: [] };
    }

    const firstConversation = conversationsCanClose[0];
    return await validateConversationCanClose(
      rpcClient,
      firstConversation._id.toString(),
      team._id.toString(),
      client,
    );
  }, [open, conversationsCanClose, rpcClient, selectAllInfo]);

  useEffect(() => {
    setConversationsCanClose(
      conversations.filter(
        (conversation) => conversation.status !== ConversationStatus.CLOSED,
      ),
    );
  }, [conversations]);

  useEffect(() => {
    if (validationResult.value) {
      setMissingTagGroups(validationResult.value.missingTagGroups);
    }
  }, [validationResult.value]);

  useEffect(() => {
    if (open && selectAllInfo?.closeImmediately) {
      void handleCloseSelectAll();
    }
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAllInfo?.closeImmediately, open]);

  const closeBatch = async (): Promise<{
    batchSize: number;
    done: boolean;
  }> => {
    if (!selectAllInfo) return { batchSize: 0, done: true };
    const { done, value } = await getNextBatch(
      selectAllInfo.bulkActionIterator,
    );
    if (done) {
      return { batchSize: 0, done };
    }
    const conversationIds = value.conversations
      .filter(
        (conversation) =>
          conversation.status !== ConversationStatus.CLOSED &&
          !selectAllInfo.deselectedConversations.some(
            (deselectedConversation) =>
              deselectedConversation._id === conversation._id,
          ),
      )
      .map((conversation) => conversation._id);
    if (conversationIds.length > 0) {
      await bulkUpdateConversationsStatus(client, {
        conversationIds,
        status: ConversationStatus.CLOSED,
      });
    }
    return { batchSize: value.conversations.length, done: false };
  };

  // This function synchronously closes each page of conversations.
  // If speed becomes an issue, we could asynchronously close each page.
  const handleCloseSelectAll = async () => {
    if (!selectAllInfo) return;
    setClosingSelectAll(true);
    selectAllInfo.setBlockRefresh(true);
    let done = false;
    do {
      const result = await closeBatch();
      setNumDone((prev) => prev + result.batchSize);
      done = result.done;
    } while (!done);
    amplitude.logEvent("close-conversation", { mode: "all" });
    setClosingSelectAll(false);
    selectAllInfo.setBlockRefresh(false);
    cleanupFn?.(true);
    setOpen(false);
  };

  const plural = conversationsCanClose.length > 1 || selectAllInfo;
  const canClose = validationResult.value?.canClose ?? true;
  const hasValidationError = missingTagGroups.length > 0;

  return (
    <RedoModal
      isOpen={open}
      modalSize="sm"
      onModalCloseRequested={() => {
        cleanupFn?.(false);
        setOpen(false);
      }}
      primaryButton={
        closingSelectAll
          ? undefined
          : {
              text: `Yes, close ticket${plural ? "s" : ""}`,
              onClick: async () => {
                if (doNotShowAgain) {
                  localStorage.setItem(
                    "redo.doNotShowCloseTicketModal",
                    "true",
                  );
                }
                if (selectAllInfo) {
                  await handleCloseSelectAll();
                } else {
                  await handleCloseTickets({
                    client,
                    conversations: conversationsCanClose,
                    setActiveConversation,
                    team,
                    inDetailView,
                    cleanupFn,
                    removeConversation,
                    nextConversationInList,
                    prevConversationInList,
                  });
                  setOpen(false);
                }
              },
              disabled: !canClose || validationResult.pending,
              size: "sm",
            }
      }
      secondaryButton={
        closingSelectAll
          ? undefined
          : { text: "Cancel", onClick: () => setOpen(false), size: "sm" }
      }
      title={
        closingSelectAll
          ? "Closing tickets"
          : `Close ${
              selectAllInfo
                ? "all selected "
                : plural
                  ? conversationsCanClose.length + " "
                  : ""
            }ticket${plural ? "s" : ""}?`
      }
    >
      {closingSelectAll ? (
        <RedoProgressBar
          percentage={(numDone / (selectAllInfo?.count || numDone)) * 100}
        />
      ) : (
        <div className={closeTicketModalCss.actionModalContentContainer}>
          {hasValidationError ? (
            <div>
              <div style={{ marginBottom: "12px", color: "#e53e3e" }}>
                This conversation cannot be closed because it's missing required
                tags from the following groups:
              </div>
              <ul style={{ marginBottom: "12px", paddingLeft: "20px" }}>
                {missingTagGroups.map((group) => (
                  <li key={group._id} style={{ marginBottom: "4px" }}>
                    {group.name}
                  </li>
                ))}
              </ul>
              <div>
                Please add tags from these groups before closing this
                conversation.
              </div>
            </div>
          ) : (
            <>
              <div>
                {plural ? "These tickets" : "This ticket"} can be reopened if
                needed.
              </div>
              <RedoCheckbox
                label="Don't show this again"
                setValue={(val) => setDoNotShowAgain(!!val)}
                size="sm"
                value={doNotShowAgain}
              />
            </>
          )}
        </div>
      )}
    </RedoModal>
  );
});
